'use client';

import { Spinner } from '@common/components';
import type { S3_PATH_TYPE } from '@common/constants/s3';
import { useToast } from '@common/hooks';
import SvgAdd from '@common/icon/SvgAdd';
import SvgCameraFill from '@common/icon/SvgCameraFill';
import SvgTextfieldclose from '@common/icon/SvgTextfieldclose';
import { cn } from '@core/utils';
import { useMutation } from '@tanstack/react-query';
import { AuthApi } from '@/api';
import { Image } from '@/components/client';
import { useFileUpload } from '@/hooks/useFileUpload';
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'gif', 'png'];
const VIDEO_EXTENSIONS = ['mp4', 'wma', 'mov'];
const IMAGE_ACCEPT = 'image/jpeg,image/jpg,image/gif,image/png,image/heic,image/heif,.heic,.heif';
const VIDEO_ACCEPT = 'video/mp4,video/x-ms-wma,video/quicktime';
const DEFAULT_MAX_BYTES = 30 * 1024 * 1024;
const getFileExtension = (file: File) => file.name.split('.').pop()?.toLowerCase() ?? '';
const isVideoUrl = (url: string) => {
  const ext = url.split('.').pop()?.toLowerCase() ?? '';
  return VIDEO_EXTENSIONS.includes(ext);
};
interface MultiFileUploadProps {
  fileUrls: string[];
  onChange: (urls: string[]) => void;
  s3PathType: `${S3_PATH_TYPE}`;
  maxFiles?: number;
  maxBytes?: number;
  allowVideo?: boolean;
  multiple?: boolean;
  emptyState?: React.ReactNode;
  guidelines?: string[];
}
export const MultiFileUpload = ({
  fileUrls,
  onChange,
  s3PathType,
  maxFiles = 5,
  maxBytes = DEFAULT_MAX_BYTES,
  allowVideo = false,
  multiple = false,
  emptyState,
  guidelines
}: MultiFileUploadProps) => {
  const {
    toast
  } = useToast();
  const acceptTypes = allowVideo ? `${IMAGE_ACCEPT},${VIDEO_ACCEPT}` : IMAGE_ACCEPT;
  const allowedExtensions = allowVideo ? [...IMAGE_EXTENSIONS, ...VIDEO_EXTENSIONS] : IMAGE_EXTENSIONS;
  const {
    getFile,
    getFiles
  } = useFileUpload({
    accept: acceptTypes,
    multiple
  });
  const {
    mutateAsync: uploadFile,
    isPending: isUploading
  } = useMutation({
    mutationFn: (file: File) => AuthApi['post:/wello/v2/auth/s3']({
      body: {
        fileUrlList: [file],
        s3CdnTypeCd: 'S3_CDN_TYPE_WELFAREHELLO',
        type: s3PathType
      }
    })
  });
  const validateFile = (file_0: File) => {
    const ext = getFileExtension(file_0);
    if (!allowedExtensions.includes(ext)) {
      toast({
        message: '허용되지 않는 파일 형식입니다.',
        type: 'warning'
      });
      return false;
    }
    if (file_0.size > maxBytes) {
      toast({
        message: `파일 용량은 ${maxBytes / 1_024 / 1_024}MB 이하여야 합니다.`,
        type: 'warning'
      });
      return false;
    }
    return true;
  };
  const handleUpload = async () => {
    try {
      if (multiple) {
        const selectedFiles = await getFiles();
        if (selectedFiles.length === 0) return;
        const remainingSlots = maxFiles - fileUrls.length;
        const filesToUpload = selectedFiles.slice(0, remainingSlots);
        const validFiles = filesToUpload.filter(validateFile);
        if (validFiles.length === 0) return;
        const uploadedUrls: string[] = [];
        for (const file_1 of validFiles) {
          const {
            data
          } = await uploadFile(file_1);
          const fileUrl = data?.context?.contents?.[0].fileUrl;
          if (fileUrl) {
            uploadedUrls.push(fileUrl);
          }
        }
        if (uploadedUrls.length > 0) {
          onChange([...fileUrls, ...uploadedUrls]);
        }
      } else {
        const file_2 = await getFile();
        if (!file_2) return;
        if (!validateFile(file_2)) return;
        const {
          data: data_0
        } = await uploadFile(file_2);
        const fileUrl_0 = data_0?.context?.contents?.[0].fileUrl;
        if (!fileUrl_0) {
          toast({
            message: '파일 업로드를 실패했습니다.',
            type: 'fail'
          });
          return;
        }
        onChange([...fileUrls, fileUrl_0]);
      }
    } catch {
      toast({
        message: '파일 업로드에 실패했습니다.',
        type: 'fail'
      });
    }
  };
  const handleDelete = (index: number) => {
    onChange(fileUrls.filter((_, fileIndex) => fileIndex !== index));
  };
  const hasFiles = fileUrls.length > 0;
  const canAddMore = fileUrls.length < maxFiles;
  return <div>
      {hasFiles ? <ul className="flex flex-wrap gap-8">
          {fileUrls.map((fileUrl_1, index_0) => <li key={fileUrl_1} className="relative">
              {isVideoUrl(fileUrl_1) ? <div className="flex size-72 items-center justify-center border border-Gray-black/5 bg-Gray-100">
                  <SvgCameraFill className="size-20 text-Gray-400" />
                </div> : <Image src={fileUrl_1} alt="첨부 이미지" width={72} height={72} className="size-72 border border-Gray-black/5 object-cover" />}
              <button type="button" onClick={() => handleDelete(index_0)} className="absolute -right-8 -top-8">
                <SvgTextfieldclose className="size-20" originalFill />
              </button>
            </li>)}
          {canAddMore ? <li>
              <button type="button" onClick={handleUpload} disabled={isUploading} className="flex size-72 items-center justify-center border border-Gray-black/5 bg-Gray-100">
                {isUploading ? <Spinner size={14} /> : <SvgAdd className="size-20 text-Gray-400" />}
              </button>
            </li> : null}
        </ul> : emptyState ? <button type="button" onClick={handleUpload} disabled={isUploading} className="flex w-full flex-col items-center gap-4 border border-dashed border-Gray-700 bg-Gray-100 py-24 hover:bg-Gray-100/70">
          {isUploading ? <Spinner size={24} /> : emptyState}
        </button> : <button type="button" onClick={handleUpload} disabled={isUploading} className="flex size-72 items-center justify-center border border-Gray-black/5 bg-Gray-100">
          {isUploading ? <Spinner size={14} /> : <SvgAdd className="size-20 text-Gray-400" />}
        </button>}

      {guidelines ? <ul className={cn('mt-16 font-400 text-Gray-600 typo-caption1', "[&>li]:flex [&>li]:items-center [&>li]:gap-8 [&>li]:before:content-['•']")}>
          {guidelines.map(text => <li key={text}>{text}</li>)}
        </ul> : null}
    </div>;
};